import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { formatDateTime, todayForFileName } from '@shared/utils/dateUtils';

import { COLUMN_SEARCH_TYPES, PAGINATION_SIZE } from '@constants/index';
import ExportTableData from '@shared/components/ExportTableData/ExportTableData';
import PageView from '@shared/components/PageView';
import TableView from '@shared/components/table/TableView';
import { USERS_REPORT_VIEW_DEFAULT_HEADERS } from '@shared/tableHeaders/reports';
import { formatFullName, getLabel } from '@shared/utils';

const UsersReportView = ({
  fetchUsers,
  title,
  renderRoleFilter,
  initialSearch: initialSearchProp,
  headers: headersProp,
  columnsSearch: columnsSearchProp,
  rowFormat: rowFormatProp,
  formatExportData: formatExportDataProp,
}) => {
  const defaultSearch = { user: '', subsidiary: '', role: '' };
  const [loading, setLoading] = useState(true);
  const [usersList, setUsersList] = useState([]);
  const [search, setSearch] = useState(initialSearchProp ?? defaultSearch);
  const [currentPage, setCurrentPage] = useState(0);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_SIZE);
  const [sorting, setSorting] = useState({});

  const rowFormat =
    rowFormatProp ??
    ((data) => {
      return (data || []).map((users) => ({
        user: users.user_mail,
        name: formatFullName(users.user_name, users.user_lastname),
        profile_state: users.profile_enable ? 'HABILITADO' : 'INHABILITADO',
        subsidiary: users.subsidiary_name,
        role: users.role_name,
      }));
    });

  const syncUsers = async () => {
    setLoading(true);
    const response = await fetchUsers(search, 0, 0, pageSize, sorting);
    const { data_set, rows_count } = response?.data || {};
    setCurrentPage(0);
    setCount(rows_count ?? 0);
    setUsersList(rowFormat(data_set));
    setLoading(false);
  };

  useEffect(() => {
    syncUsers();
  }, [search, sorting]);

  const nextPage = async (page, selectedSize) => {
    setPageSize(selectedSize);
    const response = await fetchUsers(search, 0, page, selectedSize, sorting);
    const { data_set: listUsers, rows_count } = response?.data || {};
    setCurrentPage(page);
    setCount(rows_count ?? 0);
    setUsersList(rowFormat(listUsers));
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
  };

  const formatExportData =
    formatExportDataProp ??
    ((data) => {
      return (data || []).map((user) => ({
        [getLabel('user')]: user.user_mail,
        [getLabel('name')]: formatFullName(user.user_name, user.user_lastname),
        [getLabel('profileState')]: user.profile_enable ? 'HABILITADO' : 'INHABILITADO',
        [getLabel('correspondent')]: user.correspondent_name,
        [getLabel('subsidiary')]: user.subsidiary_name,
        [getLabel('role')]: user.role_name,
        [getLabel('profileCreatedAt')]: user.profile_created_at ? formatDateTime(user.profile_created_at) : '',
        [getLabel('profileDisabledAt')]: user.profile_disabled_at ? formatDateTime(user.profile_disabled_at) : '',
      }));
    });

  const onSearch = (name, value) => {
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const defaultColumnsSearch = [
    {
      name: 'user',
      label: getLabel('user'),
      onSearch,
      type: COLUMN_SEARCH_TYPES.EMAIL,
    },
    {
      name: 'subsidiary',
      label: getLabel('subsidiary'),
      onSearch,
      type: COLUMN_SEARCH_TYPES.TEXT,
    },
    ...(renderRoleFilter
      ? [
          {
            name: 'role',
            label: getLabel('role'),
            onSearch,
            component: renderRoleFilter(onSearch),
          },
        ]
      : []),
  ];
  const columnsSearch = (columnsSearchProp ?? defaultColumnsSearch).map((col) => ({
    ...col,
    onSearch: col.onSearch ?? onSearch,
    ...(col.name === 'role' && renderRoleFilter && { component: renderRoleFilter(onSearch) }),
  }));

  const headers = headersProp ?? USERS_REPORT_VIEW_DEFAULT_HEADERS;

  const createTable = (rows = []) => (
    <TableView
      isLoading={loading}
      pagination={{
        pageNumber: currentPage,
        handleChangePage: nextPage,
        handleChangeRowsPerPage: nextPage,
        rowsTotalCount: Number(count),
      }}
      columnsSearch={columnsSearch}
      headers={headers}
      rows={rows}
      mapRowData={(row) => ({ ...row })}
      onSortingChange={handleSortingChange}
    />
  );

  const getReportDataToExport = async () => {
    const { data } = await fetchUsers(search, 0, 0, count, sorting);
    return formatExportData(data?.data_set || []);
  };

  const pageTitle = title ?? getLabel('usersReport');

  return (
    <PageView title={pageTitle}>
      <PageView.Actions>
        <ExportTableData getData={getReportDataToExport} fileName={`reporte-usuarios-${todayForFileName()}`} />
      </PageView.Actions>
      <PageView.Content>{createTable(usersList)}</PageView.Content>
    </PageView>
  );
};

UsersReportView.propTypes = {
  fetchUsers: PropTypes.func.isRequired,
  title: PropTypes.string,
  renderRoleFilter: PropTypes.func,
  initialSearch: PropTypes.object,
  headers: PropTypes.array,
  columnsSearch: PropTypes.array,
  rowFormat: PropTypes.func,
  formatExportData: PropTypes.func,
};

export default UsersReportView;
